import {
	InfiniteData,
	QueryObserverOptions,
	useInfiniteQuery,
	useQuery,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { AuditService } from '../../lib/api/audit.services'
import {
	AuditEvent,
	GetAuditEventsParamsProps,
	GetAuditSchemeDataResponse,
	GetEventDetailsDataResponse,
	GetEventsDataResponse,
} from '../../lib/api/types/audit.interfaces'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { useMemo } from 'react'

const AUDIT_EVENTS_CHUNK_SIZE = 100

type AuditEventsCursor = {
	lastDate: string
	lastId: string
} | null

type UseAuditEventsParams = GetAuditEventsParamsProps & {
	offset?: number
}

export const useAuditEvents = ({
	offset = 0,
	limit = 10,
	filters,
	query,
	orderBy,
	orderDirection,
}: UseAuditEventsParams) => {
	const { isAuthenticated } = useAuth()

	const filtersKey = useMemo(() => {
		const f = filters || {}
		return JSON.stringify({
			query,
			categoryId: f?.categoryId || [],
			objectId: f?.objectId || [],
			userId: f?.userId || [],
			startDate: f?.startDate,
			endDate: f?.endDate,
			orderBy,
			orderDirection,
		})
	}, [filters, query, orderBy, orderDirection])
	const stableFilters = useMemo<GetAuditEventsParamsProps['filters']>(() => {
		const f = filters || {}
		return {
			categoryId: f?.categoryId || [],
			objectId: f?.objectId || [],
			userId: f?.userId || [],
			startDate: f?.startDate,
			endDate: f?.endDate,
		}
	}, [filtersKey])

	const auditEventsQuery = useInfiniteQuery<
		GetEventsDataResponse,
		Error,
		InfiniteData<GetEventsDataResponse, AuditEventsCursor>,
		string[],
		AuditEventsCursor
	>({
		queryKey: ['audit-events', filtersKey],
		initialPageParam: null,
		queryFn: ({ pageParam }) =>
			AuditService.getEvents({
				query,
				filters: stableFilters,
				orderBy: orderBy || undefined,
				orderDirection: orderDirection !== null ? orderDirection : undefined,
				includeTotalCount: pageParam ? undefined : true,
				...(pageParam
					? {
							lastDate: pageParam.lastDate,
							lastId: pageParam.lastId,
						}
					: {}),
				limit: AUDIT_EVENTS_CHUNK_SIZE,
			}),
		getNextPageParam: (lastPage): AuditEventsCursor | undefined => {
			const pagination = lastPage.data.pagination
			if (!pagination.hasMore || !pagination.lastDate || !pagination.lastId) {
				return undefined
			}

			return {
				lastDate: pagination.lastDate,
				lastId: pagination.lastId,
			}
		},
		enabled: isAuthenticated,
		retry: 1,
	})

	const pages = useMemo(
		() => auditEventsQuery.data?.pages ?? [],
		[auditEventsQuery.data],
	)
	const loadedEntries = useMemo<AuditEvent[]>(() => {
		return pages.flatMap((page) => page.data.entries)
	}, [pages])

	const firstPage = pages[0]
	const lastPage = pages.length ? pages[pages.length - 1] : undefined
	const totalCount = firstPage?.data.pagination.totalCount ?? 0
	const entries = useMemo(() => {
		return loadedEntries.slice(offset, offset + limit)
	}, [limit, loadedEntries, offset])
	const hasLoadedNextPage = offset + limit < loadedEntries.length
	const hasNextPage =
		hasLoadedNextPage ||
		Boolean(
			auditEventsQuery.hasNextPage &&
			(totalCount === 0 || offset + limit < totalCount),
		)

	return {
		data: {
			entries,
			loadedEntries,
			loadedCount: loadedEntries.length,
			pagination: {
				lastDate: lastPage?.data.pagination.lastDate ?? null,
				lastId: lastPage?.data.pagination.lastId ?? null,
				limit,
				hasMore: Boolean(auditEventsQuery.hasNextPage),
				totalCount,
			},
		},
		fetchNextPage: auditEventsQuery.fetchNextPage,
		hasNextChunk: Boolean(auditEventsQuery.hasNextPage),
		hasNextPage,
		hasPreviousPage: offset > 0,
		isLoading: auditEventsQuery.isLoading,
		isUpdating:
			auditEventsQuery.isFetching || auditEventsQuery.isFetchingNextPage,
		isFetchingNextPage: auditEventsQuery.isFetchingNextPage,
		refetch: auditEventsQuery.refetch,
		error: auditEventsQuery.error,
	}
}

export const useAuditEventDetails = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetEventDetailsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetEventDetailsDataResponse>({
		queryKey: ['auditEvent', id],
		queryFn: () => AuditService.getEventDetailsById({ id }),
		enabled: isAuthenticated && Boolean(id),
		retry: 1,
		...options,
	})
}

export const useAuditScheme = (
	options?: Partial<QueryObserverOptions<GetAuditSchemeDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetAuditSchemeDataResponse>({
		queryKey: ['auditScheme'],
		queryFn: () => AuditService.getScheme(),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

// util
export function buildAuditQueryParams(params: any) {
	const query: any = {}

	if (params.filters) {
		const { startDate, endDate, userId, objectId, categoryId } = params.filters

		if (startDate) query.startDate = startDate
		if (endDate) query.endDate = endDate

		if (Array.isArray(userId)) {
			userId.forEach((id: string | number, index: number) => {
				query[`userId[${index}]`] = id
			})
		}
		if (Array.isArray(objectId)) {
			objectId.forEach((id: string | number, index: number) => {
				query[`objectId[${index}]`] = id
			})
		}
		if (Array.isArray(categoryId)) {
			categoryId.forEach((id: string | number, index: number) => {
				query[`categoryId[${index}]`] = id
			})
		}
		if (startDate || endDate) {
			query.timezoneOffset = new Date().getTimezoneOffset()
		}
	}

	for (const key in params) {
		if (key !== 'filters' && params[key] !== undefined) {
			query[key] = params[key]
		}
	}

	return query
}
